export enum Format {
  AREA = 'AREA',
  /**
   * Default format for a percentage.
   * One decimal place and adds % symbol.
   * No abbreviation for large numbers.
   *
   * NB: percentages should be passed as a fraction e.g. 0.5 will be formatted at 50.0%, not 0.5%.
   */
  PERCENTAGE = 'PERCENTAGE',

  /**
   * Default format for a signed percentage.
   * One decimal place and adds % symbol.
   * No abbreviation for large numbers.
   *
   * NB: percentages should be passed as a fraction e.g. 0.5 will be formatted at +50.0%, not 0.5%.
   */
  SIGNED_PERCENTAGE = 'SIGNED_PERCENTAGE',

  /**
   * Default format for a monetary amount *with currency*.
   * Two decimal places, if required.
   * Abbreviation for large numbers (minimal thresholds - 99,000 becomes 99k).
   * Uses a "-" for negative numbers.
   * This will automatically add a non-breaking space (\xa0) between the currency and the number
   */
  MONEY = 'MONEY',

  /**
   * Default format for a monetary amount in a table *with currency*.
   * Two decimal places, if required.
   * Abbreviation for large numbers (minimal thresholds - 99,000 becomes 99k).
   * Uses parentheses for negative numbers.
   * This will automatically add a non-breaking space (\xa0) between the currency and the number
   */
  MONEY_TABLES = 'MONEY_TABLES',

  /**
   * Default format for a monetary amount outside *with currency*.
   * Two decimal places, if required.
   * Abbreviation for large numbers (thresholds are 100x - 99,000 doesn't become 99k).
   * Uses a "-" for negative numbers.
   * This will automatically add a non-breaking space (\xa0) between the currency and the number
   */
  MONEY_CUSTOM_ABBREVIATION = 'MONEY_CUSTOM_ABBREVIATION',

  /**
   * Default format for a monetary amount in a table *with currency*.
   * Two decimal places, if required.
   * Abbreviation for large numbers (thresholds are 100x - 99,000 doesn't become 99k).
   * Uses parentheses for negative numbers.
   * This will automatically add a non-breaking space (\xa0) between the currency and the number
   */
  MONEY_TABLES_CUSTOM_ABBREVIATION = 'MONEY_TABLES_CUSTOM_ABBREVIATION',

  /**
   * Default format for an abbreviated monetary amount.
   * One decimal place.
   * No abbreviation.
   *
   * To be used when the value is abbreviated but the abbreviation and currency are displayed elsewhere,
   *  such as in a table column header or a chart legend
   */
  MONEY_ABBREVIATED = 'MONEY_ABBREVIATED',

  /**
   * No decimals.
   * No abbreviation.
   * Uses parentheses for negative numbers.
   *
   * To be used when the value is abbreviated but the abbreviation and currency are displayed elsewhere,
   *  such as in a table column header or a chart legend
   */
  MONEY_TABLES_ABBREVIATED_ONES = 'MONEY_TABLES_ABBREVIATED_ONES',

  /**
   * Default format for an abbreviated monetary amount.
   * One decimal place.
   * No abbreviation.
   * Uses parentheses for negative numbers.
   *
   * To be used when the value is abbreviated but the abbreviation and currency are displayed elsewhere,
   *  such as in a table column header or a chart legend
   */
  MONEY_TABLES_ABBREVIATED = 'MONEY_TABLES_ABBREVIATED',

  /**
   * Two decimal places.
   * No abbreviation.
   * Uses parentheses for negative numbers.
   *
   * To be used when the value is abbreviated but the abbreviation and currency are displayed elsewhere,
   *  such as in a table column header or a chart legend
   */
  MONEY_TABLES_ABBREVIATED_HUNDREDTHS = 'MONEY_TABLES_ABBREVIATED_HUNDREDTHS',

  /**
   * Format for a monetary amount that will always be rounded to the trillions
   * (and include the trillions indicator)
   * One decimal place
   * Does not include currency
   */
  MONEY_TRILLIONS = 'MONEY_TRILLIONS',
  /**
   * Format for a monetary amount that will always be rounded to the trillions
   * (and include the trillions indicator)
   * One decimal place
   * Includes currency
   */
  MONEY_TRILLIONS_CURRENCY = 'MONEY_TRILLIONS_CURRENCY',
  /**
   * Format for a monetary amount that will always be rounded to the billions
   * (and include the billions indicator)
   * One decimal place
   * Does not include currency
   */
  MONEY_BILLIONS = 'MONEY_BILLIONS',
  /**
   * Format for a monetary amount that will always be rounded to the billions
   * (and include the billions indicator)
   * One decimal place
   * Includes currency
   */
  MONEY_BILLIONS_CURRENCY = 'MONEY_BILLIONS_CURRENCY',
  /**
   * Format for a monetary amount that will always be rounded to the millions
   * (and include the millions indicator)
   * One decimal place
   * Does not include currency
   */
  MONEY_MILLIONS = 'MONEY_MILLIONS',
  /**
   * Format for a monetary amount that will always be rounded to the millions
   * (and include the millions indicator)
   * One decimal place
   * Includes currency
   */
  MONEY_MILLIONS_CURRENCY = 'MONEY_MILLIONS_CURRENCY',
  /**
   * Format for a monetary amount that will always be rounded to the thousands
   * (and include the thousands indicator)
   * One decimal place
   * Does not include currency
   */
  MONEY_THOUSANDS = 'MONEY_THOUSANDS',
  /**
   * Format for a monetary amount that will always be rounded to the thousands
   * (and include the thousands indicator)
   * One decimal place
   * Includes currency
   */
  MONEY_THOUSANDS_CURRENCY = 'MONEY_THOUSANDS_CURRENCY',
  /**
   * Format for a monetary amount that will never be abbreviated
   * Zero or two decimal places, depending on value
   * Does not include currency
   */
  MONEY_ONES = 'MONEY_ONES',
  /**
   * Format for a monetary amount that will never be abbreviated
   * Zero or two decimal places, depending on value
   * Includes currency
   */
  MONEY_ONES_CURRENCY = 'MONEY_ONES_CURRENCY',

  /**
   * Default format for a ratio.
   * Two decimal places.
   * No abbreviation for large numbers.
   * Adds x as a suffix.
   */
  RATIO = 'RATIO',

  /**
   * Default format for a full number with decimal places
   * Up to 2 decimal places.
   * Abbreviation for large numbers (minimal thresholds - 99,000 becomes 99k).
   */
  NUMBER = 'NUMBER',

  /**
   * Default format for a full number with decimal places
   * Up to 2 decimal places.
   * Abbreviation for large numbers (thresholds are 100x - 99,000 doesn't become 99k).
   */
  NUMBER_CUSTOM_ABBREVIATION = 'NUMBER_CUSTOM_ABBREVIATION',

  /**
   * Default format for a full number with zero precision.
   * No decimal places.
   * Abbreviation for large numbers (minimal thresholds - 99,000 becomes 99k).
   */
  INTEGER = 'INTEGER',

  /**
   * Default format for a full number with zero precision.
   * No decimal places.
   * No abbreviation for large numbers
   */
  INTEGER_NO_ABBREVIATION = 'INTEGER_NO_ABBREVIATION',

  /**
   * Default format for a full number + or - symbol.
   * No abbreviation for large numbers
   */
  SIGNED_INTEGER = 'SIGNED_INTEGER',

  /**
   * Default format for a full number with zero precision.
   * No decimal places.
   * Abbreviation for large numbers (thresholds are 100x - 99,000 doesn't become 99k).
   */
  INTEGER_CUSTOM_ABBREVIATION = 'INTEGER_CUSTOM_ABBREVIATION',

  /**
   * Format for a year as a number (e.g. 2022)
   * No decimal places
   * No thousand separator
   * No abbreviation
   */
  YEAR = 'YEAR',

  /**
   * Format for relative date formatting from the current date.
   * e.g. 2 days ago, in 3 weeks etc.
   */
  FROM_NOW = 'FROM_NOW',

  /**
   * Format for a date without a specific year, useful for e.g. financial year end.
   * Equivalent to the locale's short date format with the year removed
   * e.g. DD/MM for UK English, MM/DD for US English
   */
  DATE_WITHOUT_YEAR = 'DATE_WITHOUT_YEAR',

  /**
   * Format for a date without a specific year, useful for e.g. financial year end.
   * Equivalent to the locale's medium date format with the year removed
   * e.g. 31 Dec for UK English, Dec 31 for US English
   */
  DATE_MEDIUM_WITHOUT_YEAR = 'DATE_MEDIUM_WITHOUT_YEAR',

  /**
   * Format for a date with month and year only
   * Currently always in the form e.g. Jan 2021, with the month translated as relevant,
   * but order fixed as month-year, which may not be appropriate in all locales.
   * e.g. Dec 2021
   */
  DATE_MONTH_YEAR = 'DATE_MONTH_YEAR',

  /**
   * Format for a date with month and year only
   * Currently always in the form e.g. 01/21, with the month translated as relevant,
   * but order fixed as month-year, which may not be appropriate in all locales.
   * e.g. Dec 2021
   */
  DATE_MONTH_YEAR_SHORT = 'DATE_MONTH_YEAR_SHORT',

  /**
   * Format for a date with year only
   * e.g. 2021, 2019 etc
   * Note: works only with a Date object
   */
  DATE_YEAR_ONLY = 'DATE_YEAR_ONLY',

  /**
   * Localised short date format.
   * British English: 25/12/2019
   * US English: 12/25/2019
   */
  DATE_SHORT = 'DATE_SHORT',

  /**
   * Localised medium date format
   * British English: 25 Sep 2019
   * US English: Sep 25, 2019
   */
  DATE_MEDIUM = 'DATE_MEDIUM',

  /**
   * Localised medium date format with time
   * US: Sep 25, 2020 2:10 PM
   * UK: 25 Sep 2020 14:10
   * Germany: 25. Sep. 2019 14:22
   */
  DATE_MEDIUM_WITH_TIME = 'DATE_MEDIUM_WITH_TIME',

  /**
   * Localised long date format.
   * British English: 25 December 2019
   * US English: December 25, 2019
   */
  DATE_LONG = 'DATE_LONG',

  /**
   * Localised long date format with time
   * British English: 25 December 2019 14:22
   * US English: December 25, 2019 2:22 PM
   */
  DATE_LONG_WITH_TIME = 'DATE_LONG_WITH_TIME',

  /**
   * Localised time format
   * British English: 14:22
   * US English: 2:22 PM
   */
  TIME = 'TIME',

  /**
   * Localised time format
   * British English: 14:22:01
   * US English: 2:22:01 PM
   */
  TIME_WITH_SECONDS = 'TIME_WITH_SECONDS',

  /**
   * Non-localised time format
   * Always appear in the format HH:mm:ss.SSS (dayjs)
   * i.e. 14:22:01.456
   */
  TIME_WITH_MILLISECONDS = 'TIME_WITH_MILLISECONDS',

  /**
   * Format for relative date formatting for today and yesterday
   * Uses Format.DATE_MEDIUM format for all other dates
   * Includes time using Format.TIME format
   * i.e Yesterday, 10:15
   */
  RELATIVE_DAY = 'RELATIVE_DAY',
}
